import React, { useEffect, useState } from "react";
import {
  Typography,
  //  Grid, // REMOVIDO: Eliminando o componente Grid
  Box,
  CircularProgress,
  TextField,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  Pagination,
  Collapse,
} from "@mui/material";
import { API_URLS } from "../../config/apiUrls";
import { API_IBGE } from "../../config/apiIBGE";
import FinanceiroCard from "../FinanceiroCard";
import FilterListIcon from "@mui/icons-material/FilterList"; // Ícone para o botão de filtros

interface User {
  id: string;
  name: string;
  email: string;
}

interface Ad {
  id: string;
  title: string;
  isCountryWide: boolean;
  ufs: Array<{
    id: number;
    name: string;
    abbreviation: string;
  }>;
  cities: Array<{
    id: string;
    name: string;
  }>;
}

interface Payment {
  paymentId: string;
  financialRegister: string | null;
  status: string;
  amount: string;
  updatedAt: string;
  user: User;
  ad: Ad | null;
}

interface FinanceiroProps {
  token: string;
  role: "MASTER" | "PARTNER" | "ANALYST";
}

const Financeiro: React.FC<FinanceiroProps> = ({ token, role }) => {
  const [pagamentos, setPagamentos] = useState<Payment[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [isCountryWide, setIsCountryWide] = useState<boolean>(false);
  const [estados, setEstados] = useState<
    { id: number; sigla: string; nome: string }[]
  >([]);
  const [cidades, setCidades] = useState<
    { nome: string; codigo_ibge: string }[]
  >([]);
  const [estadoSelecionado, setEstadoSelecionado] = useState<number | "">("");
  const [cidadeSelecionada, setCidadeSelecionada] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(15);
  const [searchTermUser, setSearchTermUser] = useState<string>("");

  const [showFilters, setShowFilters] = useState<boolean>(false);

  // Funções de manipulação e busca (inalteradas)
  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number,
  ) => {
    setPage(page);
    window.scrollTo(0, 0);
  };

  const fetchStates = async () => {
    try {
      const response = await fetch(`${API_IBGE.estados}`);
      const data = await response.json();
      const sortedStates = data.sort(
        (a: { nome: string }, b: { nome: string }) => {
          return a.nome.localeCompare(b.nome);
        },
      );
      setEstados(sortedStates);
    } catch (err) {
      setError("Erro ao carregar estados");
    }
  };

  useEffect(() => {
    fetchStates();
  }, []);

  useEffect(() => {
    if (estadoSelecionado) {
      const estadoSelecionadoObj = estados.find(
        (estado) => estado.id === estadoSelecionado,
      );
      if (estadoSelecionadoObj) {
        const fetchCidades = async () => {
          try {
            const response = await fetch(
              `${API_IBGE.cidades}/${estadoSelecionadoObj.sigla}`,
            );
            const data = await response.json();
            setCidades(data);
          } catch (err) {
            setError("Erro ao buscar cidades.");
            setCidades([]);
          }
        };
        fetchCidades();
      }
    } else {
      setCidades([]);
    }
  }, [estadoSelecionado, estados]);

  useEffect(() => {
    const fetchPagamentos = async () => {
      try {
        const response = await fetch(API_URLS.pagamentos, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          const errorMessage = await response.text();
          throw new Error(errorMessage);
        }

        const data: Payment[] = await response.json();

        const pagosCompletados = data.filter(
          (payment) => payment.status === "COMPLETED" && payment.ad !== null,
        );

        setPagamentos(pagosCompletados);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Erro desconhecido.");
      } finally {
        setLoading(false);
      }
    };

    fetchPagamentos();
  }, [token]);

  const filteredPagamentos = pagamentos.filter((payment) => {
    const paymentDate = payment.updatedAt.split("T")[0];

    const isAfterStartDate = startDate ? paymentDate >= startDate : true;
    const isBeforeEndDate = endDate ? paymentDate <= endDate : true;

    // Filtro por Nome do Usuário
    let isUserMatch = true;
    if (searchTermUser) {
      const lowerCaseSearchTerm = searchTermUser.toLowerCase();
      isUserMatch = payment.user.name
        .toLowerCase()
        .includes(lowerCaseSearchTerm);
    }

    let isCountryWideFilter = true;
    let isEstadoFilter = true;
    let isCidadeFilter = true;

    // Aplica filtros de localização se o papel não for PARTNER
    if (role !== "PARTNER" && payment.ad) {
      isCountryWideFilter = isCountryWide
        ? payment.ad.isCountryWide === true
        : true;

      isEstadoFilter = cidadeSelecionada
        ? true
        : estadoSelecionado
          ? payment.ad.ufs.some(
              (uf) =>
                uf.id === estados.find((e) => e.id === estadoSelecionado)?.id,
            )
          : true;

      isCidadeFilter = cidadeSelecionada
        ? payment.ad.cities.some((city) => city.id === cidadeSelecionada)
        : true;
    } else if (role === "PARTNER") {
      isCountryWideFilter = true;
      isEstadoFilter = true;
      isCidadeFilter = true;
    }

    return (
      isAfterStartDate &&
      isBeforeEndDate &&
      isUserMatch &&
      isCountryWideFilter &&
      isEstadoFilter &&
      isCidadeFilter
    );
  });

  const clearFilters = () => {
    setStartDate("");
    setEndDate("");
    setIsCountryWide(false);
    setEstadoSelecionado("");
    setCidadeSelecionada("");
    setSearchTermUser("");
    setPage(1);
  };

  const applyFilters = () => {
    setPage(1);
  };

  const totalPagamentos = filteredPagamentos.length;
  const totalSoma = filteredPagamentos.reduce(
    (total, payment) => total + parseFloat(payment.amount),
    0,
  );

  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const pagamentosParaExibir = filteredPagamentos.slice(startIndex, endIndex);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">
          Ocorreu um erro ao carregar os dados: {error}
        </Typography>
        <Button variant="contained" onClick={() => setLoading(true)}>
          Tentar Novamente
        </Button>
      </Box>
    );
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h2" gutterBottom>
        Análise Financeira
      </Typography>

      {/* BOTÃO QUE CONTROLA A VISIBILIDADE DOS FILTROS */}
      <Box mb={2}>
        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={() => setShowFilters(!showFilters)}
        >
          {showFilters ? "Ocultar Filtros" : "Mostrar Filtros"}
        </Button>
      </Box>

      {/* COLLAPSE QUE ENVOLVE OS FILTROS ADICIONAIS */}
      <Collapse in={showFilters}>
        <Box sx={{ p: 2, border: "1px solid #ccc", borderRadius: 1, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Filtros
          </Typography>
          <Box mb={3}>
            <TextField
              label="Buscar por Nome do Usuário"
              variant="outlined"
              fullWidth
              value={searchTermUser}
              onChange={(e) => setSearchTermUser(e.target.value)}
            />
          </Box>

          {/* Filtro de intervalo de datas - SUBSTITUINDO GRID */}
          <Box mb={3} sx={{ display: "flex", gap: 2 }}>
            <Box>
              <TextField
                label="Data Inicial"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>
            <Box>
              <TextField
                label="Data Final"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Box>
          </Box>

          {/* Filtro para Pagamentos Nacionais - Condicionalmente renderizado */}
          {role !== "PARTNER" && (
            <Box mb={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isCountryWide}
                    onChange={() => setIsCountryWide(!isCountryWide)}
                  />
                }
                label="Exibir somente pagamentos de anúncios veiculados para todo Brasil"
              />
            </Box>
          )}

          {/* Filtro para Estado (UF) - Condicionalmente renderizado */}
          {role !== "PARTNER" && (
            <Box mb={3}>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={estadoSelecionado}
                  onChange={(e) =>
                    setEstadoSelecionado(e.target.value as number)
                  }
                  label="Estado"
                >
                  <MenuItem value="">
                    <em>Nenhum</em>
                  </MenuItem>
                  {estados.map((estado) => (
                    <MenuItem key={estado.id} value={estado.id}>
                      {estado.nome} ({estado.sigla})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          {/* Filtro para Cidade - Condicionalmente renderizado */}
          {role !== "PARTNER" && (
            <Box mb={3}>
              <FormControl fullWidth>
                <InputLabel>Cidade</InputLabel>
                <Select
                  value={cidadeSelecionada}
                  onChange={(e) => setCidadeSelecionada(e.target.value)}
                  label="Cidade"
                  disabled={!estadoSelecionado}
                >
                  <MenuItem value="">
                    <em>Nenhuma</em>
                  </MenuItem>
                  {cidades.map((cidade) => (
                    <MenuItem
                      key={cidade.codigo_ibge}
                      value={cidade.codigo_ibge}
                    >
                      {cidade.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}
        </Box>
        <Box sx={{ mb: 3 }}>
          <Button variant="contained" color="primary" onClick={applyFilters}>
            Aplicar Filtros
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={clearFilters}
            sx={{ ml: 2 }}
          >
            Limpar Filtros
          </Button>
        </Box>
      </Collapse>

      {/* Informações no topo */}
      <Box mb={3}>
        <Typography variant="h5" component="div">
          Total de Pagamentos: {totalPagamentos}
        </Typography>
        <Typography variant="h5" component="div">
          Soma Total: {formatCurrency(totalSoma)}
        </Typography>
      </Box>
      <Typography variant="h4" gutterBottom>
        Pagamentos Completados
      </Typography>

      {/* Lista de Cards - SUBSTITUINDO GRID CONTAINER E GRID ITEM */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          // Simula spacing={5} (40px)
          margin: "-20px",
        }}
      >
        {pagamentosParaExibir.length === 0 ? (
          <Box sx={{ textAlign: "center", width: "100%", mt: 3, mb: 3 }}>
            <Typography variant="h6" color="textSecondary">
              Não há Pagamentos para exibir com os filtros atuais.
            </Typography>
          </Box>
        ) : (
          pagamentosParaExibir.map((payment) => (
            // Substitui <Grid item xs={12} sm={6} md={4}>
            <Box
              key={payment.paymentId}
              sx={{
                padding: "20px", // Simula o spacing={5}
                // Larguras: xs={12} (100%), sm={6} (50%), md={4} (33.333%)
                width: {
                  xs: "100%",
                  sm: "50%",
                  md: "30%",
                },
              }}
            >
              <FinanceiroCard payment={payment} />
            </Box>
          ))
        )}
      </Box>

      {/* Paginação */}
      <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
        <Pagination
          count={Math.ceil(totalPagamentos / itemsPerPage)}
          page={page}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>
    </Box>
  );
};

export default Financeiro;
